import React, { useState, useCallback, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { Grid } from '@mui/material'
import {
    AnyAutocomplete,
    AnyInput,
    useValidatedForm,
    AutocompleteOption,
} from '../anyform'
import {
    createTenant,
    getTenant,
    updateTenant,
    getMetadata,
    emptyPromise,
} from '../api'
import { FormActions, Retrievable, Surface, TenantsIcon } from '../components'
import { FeedbackContext } from '../feedback'
import { translate } from '../i18n'
import { SignUpMetadataResponse, Tenant, TenantRequest } from '../model'
import { RouterParams } from '../router'

type Data = [Tenant | undefined, SignUpMetadataResponse]

export default function TenantForm({ match }: RouterParams) {
    const history = useHistory()
    const feedbackContext = useContext(FeedbackContext)
    const tenantId = match.params.tenantId

    const [tenant, setTenant] = useState<Tenant>()
    const [planOptions, setPlanOptions] = useState<AutocompleteOption[]>([])
    const { register, bind, isValid } = useValidatedForm<TenantRequest>()

    const retrievePromise = useCallback((): Promise<Tenant | undefined> => {
        if (tenantId) {
            return getTenant(tenantId)
        }
        return emptyPromise()
    }, [tenantId])

    const metadataPromise = useCallback((): Promise<SignUpMetadataResponse> => {
        return getMetadata()
    }, [])

    const fetchData = useCallback(async (): Promise<Data> => {
        const data = await Promise.all([retrievePromise(), metadataPromise()])

        return data
    }, [retrievePromise, metadataPromise])

    const setData = useCallback(([tenant, metadata]: Data) => {
        setTenant(tenant)
        setPlanOptions(
            metadata.plans.map((el) => ({
                value: el.id,
                label: el.name,
            }))
        )
    }, [])

    const submitPromise = (): Promise<Tenant> => {
        const request = bind()
        if (tenantId) {
            return updateTenant(tenantId, request)
        }
        return createTenant(request)
    }

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!isValid()) {
            return
        }

        feedbackContext.showBackdrop()
        submitPromise()
            .then((tenant) => {
                feedbackContext.showSuccess(
                    translate(`tenants.${tenantId ? 'edited' : 'created'}`, {
                        name: tenant.name,
                    }) as string
                )
                history.push(`/tenants/?search=${tenant?.name}`)
            })
            .catch((error) => {
                feedbackContext.showError(error.message)
            })
            .finally(() => {
                feedbackContext.closeBackdrop()
            })
    }

    return (
        <Retrievable
            getPromise={fetchData}
            setData={setData}
            builder={() => (
                <Grid item xs={12}>
                    <Grid container justifyContent="center" alignItems="center">
                        <Grid item xs={12} md={10} lg={7} xl={5}>
                            <Surface
                                title={translate(
                                    tenantId
                                        ? 'tenants.edit_tenant'
                                        : 'tenants.new_tenant'
                                )}
                                icon={<TenantsIcon />}
                            >
                                <form
                                    autoComplete="off"
                                    noValidate
                                    onSubmit={onSubmit}
                                >
                                    <Grid
                                        container
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Grid item xs={12}>
                                            <AnyInput
                                                type="text"
                                                name="tenant_name"
                                                initialValue={tenant?.name}
                                                label={
                                                    translate(
                                                        'auth.signup.tenant_name'
                                                    ) as string
                                                }
                                                required
                                                register={register}
                                            />
                                            <AnyAutocomplete
                                                name="plan_id"
                                                initialValue={tenant?.plan?.id}
                                                label={
                                                    translate(
                                                        'auth.signup.plan_id'
                                                    ) as string
                                                }
                                                options={planOptions}
                                                disabled={!!tenantId}
                                                required
                                                register={register}
                                            />
                                        </Grid>
                                        <FormActions id={tenantId} />
                                    </Grid>
                                </form>
                            </Surface>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        />
    )
}
